/**
 * ExpectedRevenueCard — schedule-spine forecast for paid-for-work company homes
 * (Home Redesign, 2026-06-02). Sums future time entries × resolved rate into
 * week / month / quarter buckets, with a confirmed-vs-scheduled split and the
 * top facilities. Additive to the Command Center — only mounted for entities
 * whose operating model is 'paid_for_work' (locums physicians, NPs, dentists).
 *
 * This is a COMPANY surface (the entity's expected money in) — never the You lens.
 */
import { Link } from 'react-router-dom';
import { CalendarClock, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useExpectedRevenue } from '@/hooks/useExpectedRevenue';
import { fmtCents, fmtPct, confirmedShare } from '@/lib/personal-overview';

export function ExpectedRevenueCard() {
  const { summary, loading, error } = useExpectedRevenue();

  // Non-fatal: a forecast failure never blocks the home. Render nothing.
  if (error) return null;

  if (loading) {
    return (
      <Card padding="md">
        <Skeleton className="h-5 w-48 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      </Card>
    );
  }

  // Nothing scheduled → don't clutter the home with an empty forecast.
  if (!summary || summary.totalUpcomingCents <= 0) return null;

  const confPct = confirmedShare(summary.byConfidence.confirmedCents, summary.byConfidence.scheduledCents);
  const topFacilities = (summary.byFacility || []).slice(0, 3);

  return (
    <Card padding="md" data-testid="expected-revenue-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="font-headline text-base sm:text-lg font-semibold text-slate-900 dark:text-white">
            Expected from your schedule
          </h2>
        </div>
        <Link
          to="/hours"
          className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          Schedule <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Bucket label="This week" cents={summary.thisWeekCents} />
        <Bucket label="This month" cents={summary.thisMonthCents} />
        <Bucket label="This quarter" cents={summary.thisQuarterCents} />
      </div>

      {/* Confirmed vs scheduled split */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
          <span>
            {fmtPct(confPct)} confirmed · {fmtCents(summary.byConfidence.confirmedCents)}
          </span>
          <span>{fmtCents(summary.byConfidence.scheduledCents)} scheduled</span>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full bg-primary-600 dark:bg-primary-500"
            style={{ width: `${Math.round(confPct * 100)}%` }}
          />
        </div>
      </div>

      {topFacilities.length > 0 && (
        <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-3 space-y-1.5">
          {topFacilities.map((f) => (
            <div key={f.facility} className="flex items-center justify-between text-sm">
              <span className="text-slate-600 dark:text-slate-300 truncate min-w-0 mr-3">{f.facility}</span>
              <span className="font-mono text-slate-900 dark:text-slate-100 whitespace-nowrap">
                {fmtCents(f.cents)}
                <span className="text-slate-400 dark:text-slate-500"> · {f.days}d</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="mt-3 text-[11px] text-slate-400 dark:text-slate-500">
        {summary.includedCount} upcoming entries · next 120 days
      </p>
    </Card>
  );
}

function Bucket({ label, cents }: { label: string; cents: number }) {
  return (
    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 truncate">{label}</p>
      <p className="font-mono text-base sm:text-lg font-semibold text-slate-900 dark:text-white truncate">
        {fmtCents(cents)}
      </p>
    </div>
  );
}
